import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

/** A client report's file format. */
export type ReportFileFormat = 'xlsx' | 'pdf';

/** Shown when the selected report has no PDF version. */
export const PDF_REPORTS_HINT = 'PDF is available for Attendance, Monthly ILP, Cohort Progress and Progress Overview';

/**
 * The Format field on the Reports pages (Generate and View): Excel or PDF, beside the Report dropdown.
 * PDF is disabled, with a hint, when the selected report has no PDF version. The page decides whether the
 * picker shows at all (its PDF switch), and falls the choice back to Excel when the report changes to one
 * with no PDF version.
 */
@Component({
  selector: 'app-report-format-picker',
  standalone: true,
  imports: [FormsModule, NgClass],
  template: `
    <div [attr.data-test]="testId" [ngClass]="{'tw:flex tw:items-center tw:gap-3': inline}">
      <div class="filter-bar__label" [ngClass]="{'tw:mb-0': inline}" [id]="testId + '-label'">Format</div>
      <div class="tw:flex tw:gap-3 tw:items-center tw:py-2" role="radiogroup" [attr.aria-labelledby]="testId + '-label'">
        <label class="field__radio"><input type="radio" [name]="testId" value="xlsx" [attr.data-test]="testId + '-xlsx'"
          [ngModel]="format" (ngModelChange)="formatChange.emit($event)" /> Excel</label>
        <label class="field__radio" [ngClass]="{'tw:opacity-50 tw:cursor-not-allowed': !pdfAvailable}"
          [attr.title]="inline && !pdfAvailable ? hint : null">
          <input type="radio" [name]="testId" value="pdf" [attr.data-test]="testId + '-pdf'"
          [ngModel]="format" (ngModelChange)="formatChange.emit($event)" [disabled]="!pdfAvailable" /> PDF</label>
      </div>
      @if (!pdfAvailable && !inline) {
        <span class="field__hint tw:block" [attr.data-test]="testId + '-hint'">{{ hint }}</span>
      }
    </div>
  `
})
export class ReportFormatPickerComponent {
  /** The chosen format. */
  @Input() format: ReportFileFormat = 'xlsx';
  @Output() formatChange = new EventEmitter<ReportFileFormat>();
  /** False when the selected report has no PDF version: PDF is disabled and the hint shows. */
  @Input() pdfAvailable = false;
  /** Prefix for the radio group name, the label id and the data-test hooks; unique on the page. */
  @Input() testId = 'report-format';
  /** Sits in a row of action buttons: the label goes beside the radios and the PDF hint becomes a tooltip. */
  @Input() inline = false;

  public readonly hint = PDF_REPORTS_HINT;
}
